<script setup>
import Delimiter from '@sections/delimiter.vue'
import { useStoreStore } from '@stores/store'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import _ from 'lodash'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'

import Button from '@components/global/button.vue'

const router = useRouter()
const route = useRoute()
const loading = ref(false)
const requestProduct = ref(null)
const storeStore = useStoreStore()
const selectedImage = ref(null)
const product = ref({})
const slug = computed(() => route.params.slug)
const leftSlider = ref({})
const onLeftSwiper = (swiper) => {
	leftSlider.value = swiper
}

const rightSlider = ref({})
const onRightSwiper = (swiper) => {
	rightSlider.value = swiper
}

const singleVariant = computed(() => (product.value?.variants?.length > 1 ? false : true))
const optionSelections = ref({})

const formattedOptionSelections = computed(() => {
	const options = []
	for (const [key, value] of Object.entries(optionSelections.value)) {
		options.push({
			name: key,
			value: value,
		})
	}
	return options
})

const variant = computed(() => {
	return !_.isEmpty(product.value)
		? !singleVariant.value
			? (product.value.variants.filter(function (p) {
					return JSON.stringify(_.orderBy(p.selectedOptions, ['name'], ['asc'])) == JSON.stringify(_.orderBy(formattedOptionSelections.value, ['name'], ['asc']))
				})[0] ?? {})
			: product.value.variants[0]
		: {}
})

const canAddToCart = computed(() => {
	return variant.value.id ? true : false
})

function addToCart() {
	console.log('add to cart', variant.value.id)
	storeStore.addToCart({
		id: variant.value.id,
	})
}

function cancelProduct() {
	if (requestProduct.value) requestProduct.value.cancel()
}
function getProduct() {
	loading.value = true
	optionSelections.value = {}
	product.value = {}
	cancelProduct()
	let axiosSource = axios.CancelToken.source()
	requestProduct.value = { cancel: axiosSource.cancel }
	axios
		.get(`/api/store/product/${route.params.slug}`, {
			cancelToken: axiosSource.token,
		})
		.then((p) => {
			loading.value = false
			product.value = p.data
			p.data.options?.forEach((opt) => {
				optionSelections.value[opt.name] = -1
			})
		})
		.catch((e) => {})
}
getProduct()

watch(slug, async () => {
	getProduct()
})
</script>
<template>
	<section class="container">
		<div class="flex items-center gap-4">
			<Button :simple="true" class="outlined icon" @click="router.back()">
				<i class="fa-regular fa-arrow-left h-4 w-4" />
			</Button>
			<router-link :to="{ name: 'merch' }" class="h9">merch</router-link>
			<i class="fa-solid fa-chevron-right text-[#C1C18D]" />
			<span class="h9">{{ product.title }}</span>
		</div>
	</section>
	<section class="container">
		<div v-if="product">
			<div class="grid grid-cols-1 gap-14 lg:grid-cols-2">
				<div class="sticky top-[10rem] hidden h-fit flex-row gap-6 xl:flex">
					<div class="text-default z-images flex flex-col items-center gap-4">
						<Button v-if="product.images > 3" @click="leftSlider.slidePrev()" class="outlined" :without-edge="true">
							<i class="icon-arrow-up" />
						</Button>
						<Swiper class="swiper-vertical z-images max-h-[316px] w-[100px] shrink-0 grow-0 select-none" @swiper="onLeftSwiper" @slideChange="onLeftSwiper" direction="vertical" :slides-per-view="Math.min(product.images, 3)" :space-between="12" :allowTouchMove="false" :loop="false">
							<SwiperSlide v-for="image in product.images" class="max-h-[96px] min-h-[96px]">
								<div @click="selectedImage = image" class="bg-bg relative w-fit rounded-2xl border border-[#C1C1BD]">
									<div class="w-fit cursor-pointer overflow-hidden rounded-2xl bg-bgSecondary p-3">
										<img :src="image" :alt="product.title" class="aspect-square w-[84px] object-contain" />
									</div>
								</div>
							</SwiperSlide>
						</Swiper>

						<Button v-if="product.images > 3" @click="leftSlider.slideNext()" class="outlined" :without-edge="true">
							<i class="icon-arrow-down" />
						</Button>
					</div>

					<div>
						<div class="border-border-default bg-bg relative z-images select-none rounded-[16px] border p-3">
							<div class="z-images w-fit cursor-pointer rounded-big bg-bgSecondary p-6">
								<img :src="selectedImage ?? product.image" :alt="product.title" class="aspect-square w-[400px] object-contain" />
							</div>
							<div class="bg-bg absolute left-[-1px] top-[30%] h-[40%] w-[1px]"></div>
							<div class="bg-bg absolute right-[-1px] top-[30%] h-[40%] w-[1px]"></div>
						</div>
					</div>

					<div class="absolute left-[-60vw] top-[30%] z-lines w-[100vw]">
						<DelimiterCrosses :rows="4" />
					</div>
				</div>

				<div class="relative gap-6 xl:hidden">
					<div class="border-border-default bg-bg relative z-images select-none rounded-[16px] border p-3">
						<div class="cursor-pointer rounded-big bg-bgSecondary p-6">
							<Swiper @swiper="onRightSwiper" @slideChange="onRightSwiper" :modules="[Navigation]" :navigation="true" :slides-per-view="1" :allowTouchMove="false" :loop="false">
								<SwiperSlide v-for="image in product.images">
									<div>
										<img class="aspect-square w-full object-contain" :src="image" alt="photo" />
									</div>
								</SwiperSlide>
							</Swiper>
						</div>
					</div>
					<div class="bg-bg absolute left-[-1px] top-[30%] h-[40%] w-[1px]"></div>
					<div class="bg-bg absolute right-[-1px] top-[30%] h-[40%] w-[1px]"></div>
				</div>

				<div class="text-default flex flex-col gap-8 self-center">
					<div class="flex flex-col items-start gap-3">
						<span class="text-sm capitalize text-primaryLight">{{ product.type }}</span>
						<h4 class="capitalize">{{ product.title }}</h4>
						<span class="text-sm leading-7 text-primaryLightest">
							{{ product.description }}
						</span>

						<p v-if="product.available" class="flex items-center gap-2 font-semibold">
							<span v-if="product.compare_at_price" class="rounded-2xl bg-bgSecondary p-2 text-sm text-secondaryHard line-through">
								{{ product.compare_at_price }}
							</span>
							<span class="h7">{{ product.price }}</span>
						</p>
						<p v-else class="xl text-red-base">Sold out</p>

						<div v-if="product.descriptionHtml" class="htmlmarkdown mt-5 normal-case opacity-70" v-html="product.descriptionHtml"></div>

						<div class="mb-5 mt-5 w-full border-b border-neutral-200"></div>

						<div class="grid w-full grid-cols-2 text-white md:gap-3" v-if="!singleVariant">
							<div class="flex flex-col gap-2" v-for="option in product.options" :key="option.name">
								<p class="font-bold text-xs uppercase tracking-widest text-neutral-500">
									{{ option.name }}
								</p>
								<div class="relative isolate w-full">
									<select class="w-full appearance-none rounded-full border border-border px-4 py-3 font-medium text-sm normal-case text-black placeholder:normal-case placeholder:text-primary focus:outline-none" :label="option.name" v-model="optionSelections[option.name]">
										<option value="-1" disabled>Select {{ option.name }}</option>
										<option v-for="(value, i) in option.values" :key="value" :disabled="product.options?.length == 1 && !product.variants[i].available">
											{{ value }}
											{{ product.options?.length == 1 && !product.variants[i].available ? '(Sold Out)' : '' }}
										</option>
									</select>
									<i class="fa-solid fa-chevron-down absolute right-3 top-1/2 text-sm text-black -translate-y-1/2"></i>
								</div>
							</div>
						</div>
					</div>

					<div class="flex gap-4">
						<!-- <div class="flex w-fit items-stretch rounded-full border border-border bg-bgMain px-1">
							<Button class="flex h-full w-8 items-center justify-center duration-500 disabled:text-primaryDisabled" :disabled="minusButtonDisabled" @click="changeQuantity(-1)" icon="fa-solid fa-minus"></Button>
							<div class="flex items-center px-5">
								<span>{{ product.quantity }}</span>
							</div>
							<Button class="flex h-full w-8 items-center justify-center text-primary transition-colors duration-500 disabled:text-primaryDisabled" :disabled="plusButtonDisabled" @click="changeQuantity(+1)" icon="fa-solid fa-plus"></Button>
						</div> -->
						<Button class="primary w-full transition-all duration-500 disabled:opacity-50" icon="fa-solid fa-bag-shopping" @click="addToCart" :disabled="!canAddToCart">{{ product.available ? 'Add to Cart' : 'Sold Out' }}</Button>
					</div>
				</div>
			</div>
		</div>
	</section>
	<Delimiter />
</template>
